import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, ClipboardList, ChefHat, BarChart3, Menu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface NavItem {
    icon: React.ElementType;
    label: string;
    labelAr?: string;
    href?: string;
    onClick?: () => void;
}

interface MobileBottomNavProps {
    onMoreClick: () => void;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ onMoreClick }) => {
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    const navItems: NavItem[] = [
        {
            icon: Home,
            label: 'Home',
            labelAr: 'الرئيسية',
            href: '/dashboard',
        },
        {
            icon: ClipboardList,
            label: 'Orders',
            labelAr: 'الطلبات',
            href: '/dashboard/orders',
        },
        {
            icon: ChefHat,
            label: 'Kitchen',
            labelAr: 'المطبخ',
            href: '/dashboard/kitchen',
        },
        {
            icon: BarChart3,
            label: 'Stats',
            labelAr: 'الإحصائيات',
            href: '/dashboard/analytics',
        },
        {
            icon: Menu,
            label: 'More',
            labelAr: 'المزيد',
            onClick: onMoreClick,
        },
    ];

    const isActive = (href?: string) => {
        if (!href) return false;
        if (href === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname.startsWith(href);
    };

    return (
        <nav
            className={cn(
                "fixed bottom-0 inset-x-0 z-[9998] bg-white border-t border-gray-200 md:hidden",
                "safe-area-inset-bottom"
            )}
            style={{
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
        >
            <div className="flex items-center justify-around h-16">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    const label = isRTL && item.labelAr ? item.labelAr : item.label;

                    const content = (
                        <div
                            className={cn(
                                "flex flex-col items-center justify-center gap-0.5 w-full h-full px-2 py-1",
                                "transition-colors duration-200",
                                active
                                    ? "text-indigo-600"
                                    : "text-gray-500 hover:text-gray-700 active:text-indigo-600"
                            )}
                        >
                            <Icon
                                className={cn(
                                    "h-5 w-5 transition-transform",
                                    active && "scale-110"
                                )}
                            />
                            <span
                                className={cn(
                                    "text-[10px] font-medium leading-tight",
                                    active && "font-semibold"
                                )}
                            >
                                {label}
                            </span>
                            {active && (
                                <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-indigo-600" />
                            )}
                        </div>
                    );

                    if (item.onClick) {
                        return (
                            <button
                                key={item.label}
                                onClick={item.onClick}
                                className="relative flex-1 h-full touch-manipulation"
                                aria-label={label}
                            >
                                {content}
                            </button>
                        );
                    }

                    return (
                        <Link
                            key={item.label}
                            to={item.href!}
                            className="relative flex-1 h-full touch-manipulation"
                            aria-label={label}
                            aria-current={active ? 'page' : undefined}
                        >
                            {content}
                        </Link>
                    );
                })}
            </div>
        </nav>
    );
};

export default MobileBottomNav;
